import type { Attempt, RoundConfig } from '@ranked-waiting/game-core';
import { renderPageShell } from '../lib/page-shell';
import { renderRoundStage } from '../lib/round-stage';
import { formatDurationMs, formatErrorMs } from '../lib/format';
import { resultBadgeMarkup, resultTier } from '../lib/result-badge';
import { applyCachedTimerTheme } from '../lib/cosmetics';
import { applyCachedTimerFaceArt } from '../lib/catalog';
import type { RouteHandler } from '../lib/router';

/** Exactly 10.000s, every round, no randomness — the entire point of the challenge is hitting one fixed number. */
const CHALLENGE_CONFIG: RoundConfig = {
  minTargetMs: 10_000,
  maxTargetMs: 10_000,
  minPrepareDelayMs: 400,
  maxPrepareDelayMs: 1_600,
};

export const renderChallenge10s: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'play', {
    title: '10 SECOND CHALLENGE',
    subtitle: 'Stop the clock at exactly 10.000s. How close can you get?',
    body: `
      <div class="practice-layout">
        <div class="round-mount" id="round-mount"></div>
        <aside class="practice-summary" id="practice-summary">
          <h2>Best this session</h2>
          <p class="handoff__label" id="challenge-best">No attempts yet.</p>
          <h2>Session</h2>
          <ul id="attempt-list" class="attempt-list"></ul>
          <p class="page-body__stub"><a href="#/practice">Back to free practice</a></p>
        </aside>
      </div>
    `,
  });

  const mount = body.querySelector<HTMLDivElement>('#round-mount')!;
  const attemptList = body.querySelector<HTMLUListElement>('#attempt-list')!;
  const bestEl = body.querySelector<HTMLParagraphElement>('#challenge-best')!;
  const attempts: Attempt[] = [];
  let destroyRound: (() => void) | void;

  applyCachedTimerTheme(mount);
  void applyCachedTimerFaceArt(mount);

  function renderBest() {
    const scored = attempts.filter((a) => !a.isFalseStart);
    if (scored.length === 0) {
      bestEl.textContent = 'No attempts yet.';
      return;
    }
    const best = scored.reduce((closest, a) => (a.errorMs < closest.errorMs ? a : closest));
    bestEl.textContent = `${formatErrorMs(best.errorMs)} (${scored.length} attempt${scored.length === 1 ? '' : 's'} scored)`;
  }

  function renderSummary() {
    if (attempts.length === 0) {
      attemptList.innerHTML = `<li class="attempt-list__empty">Your attempts will appear here.</li>`;
      return;
    }
    const scored = attempts.filter((a) => !a.isFalseStart);
    const bestErrorMs = scored.length > 0 ? Math.min(...scored.map((a) => a.errorMs)) : null;
    attemptList.innerHTML = attempts
      .slice()
      .reverse()
      .map((attempt, index) => {
        const isBest = bestErrorMs !== null && attempt.errorMs === bestErrorMs;
        return `
          <li class="${attempt.isFalseStart ? 'is-false-start' : ''}${isBest ? ' is-best' : ''}" data-tier="${resultTier(attempt)}">
            <span>#${attempts.length - index}</span>
            <span>${formatDurationMs(attempt.targetMs)}s target</span>
            ${resultBadgeMarkup(attempt, { highlight: isBest })}
          </li>
        `;
      })
      .join('');
  }

  function startRound() {
    destroyRound = renderRoundStage(mount, {
      config: CHALLENGE_CONFIG,
      label: '10 Second Challenge',
      onComplete: (attempt) => {
        attempts.push(attempt);
        renderSummary();
        renderBest();
        if (destroyRound) destroyRound();
        startRound();
      },
    });
  }

  renderSummary();
  renderBest();
  startRound();

  return () => {
    if (destroyRound) destroyRound();
  };
};
